import React, { type FC, type PropsWithChildren } from 'react'
import { Box, type SxProps, TableCell, TableHead, TableRow, TableSortLabel, type Theme } from '@mui/material'

import type { ReadonlyRecord } from '../../../../utils'

import { TableCheckbox } from './Checkbox'
import { ColumnLabel } from './Label'
import { ResizeGrip } from './ResizeGrip'
import {
    useColumn,
    useColumnNames,
    useHasColumnView,
    useHasTrailingColumn,
    useOnColumnViewChange,
    useSelectAll,
    useShouldShowHeader,
    useSortOrder,
    useSortOrderDispatcher,
} from './State'
import { TrailingHeaderCell } from './TrailingColumn'
import type { Columns, Line, SortOrder } from './Types'

type SortDirection = SortOrder<never>['direction']

const directionsInversion: ReadonlyRecord<SortDirection, SortDirection> = { asc: 'desc', desc: 'asc' } as const

/**
 * The label ellipsizes itself rather than the cell clipping, so the sort icon and anything on the cell's edge stay whole.
 * A block, so inside the sort label it's a flex item that can shrink below its text
 */
const oneLineLabelStyle: SxProps<Theme> = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }

/** MUI stacks its sticky header cells at this z-index */
export const STICKY_HEADER_Z_INDEX = 2

/**
 * A grip reaches past its cell's edge into the next header cell, which would paint over it, so each header cell stacks above the one after it.
 * The head is a single layer at MUI's sticky z-index, so these values only order the header cells among themselves
 */
const resizableHeadStyle: SxProps<Theme> = { position: 'relative', zIndex: STICKY_HEADER_Z_INDEX }

const SelectionCell = (): ReturnType<FC> => {
    const [selection, toggle] = useSelectAll()

    return selection
        ? (
            <TableCell key="table-header-checkbox" padding="checkbox">
                <TableCheckbox data-testid="checkbox-all" indeterminate={selection.onlySome} checked={selection.all} onChange={toggle} />
            </TableCell>
        )
        : null
}

const HeaderLabel = <L extends Line>({ name }: PropsWithChildren<{ name: keyof Columns<L> }>): ReturnType<FC> => {
    const currentSort = useSortOrder<L>()
    const setSort = useSortOrderDispatcher<L>()
    const { sort = false } = useColumn<L, keyof Columns<L>>(name)
    const hasColumnView = useHasColumnView()

    const label = hasColumnView
        ? (
            <Box component="span" data-testid={`column-header-label-${String(name)}`} sx={oneLineLabelStyle}>
                <ColumnLabel<L> name={name} />
            </Box>
        )
        : <ColumnLabel<L> name={name} />

    if (!sort) {
        return <>{label}</>
    }

    const active = currentSort?.name === name

    /**
     * If active, then use configuration, otherwise, settle for ascending
     */
    const currentDirection: SortDirection | false = active ? currentSort.direction : 'asc'

    return (
        <TableSortLabel
            active={active}
            direction={currentDirection}
            onClick={() =>
                setSort({
                    name,
                    /**
                     * If active, then invert, otherwise, use the currently displayed direction
                     */
                    direction: active ? directionsInversion[currentDirection] : currentDirection,
                })}
        >
            {label}
        </TableSortLabel>
    )
}

const HeaderCell = <L extends Line>({
    name,
    resizable,
    zIndex,
}: PropsWithChildren<{ name: keyof Columns<L>, resizable: boolean, zIndex: number | undefined }>): ReturnType<FC> => {
    const { type = null } = useColumn<L, keyof Columns<L>>(name)
    const onColumnViewChange = useOnColumnViewChange()

    return (
        <TableCell align={type === 'number' ? 'right' : undefined} data-testid={`column-header-${name}`} style={zIndex === undefined ? undefined : { zIndex }}>
            <HeaderLabel name={name} />
            {resizable && onColumnViewChange && <ResizeGrip name={name} onChange={onColumnViewChange} />}
        </TableCell>
    )
}

export const Header = <L extends Line>(): ReturnType<FC> => {
    const names = useColumnNames<L>()
    const shouldShow = useShouldShowHeader()
    const hasTrailingColumn = useHasTrailingColumn()
    const hasColumnView = useHasColumnView()

    if (!shouldShow) {
        return null
    }

    return (
        <TableHead sx={hasColumnView ? resizableHeadStyle : undefined}>
            <TableRow>
                <SelectionCell />
                {names.map((name, index) => (
                    /** The last column absorbs the remaining width, so it has no edge of its own to drag */
                    <HeaderCell
                        key={String(name)}
                        name={name}
                        resizable={index < names.length - 1}
                        zIndex={hasColumnView ? STICKY_HEADER_Z_INDEX + names.length - index : undefined}
                    />
                ))}
                {hasTrailingColumn && <TrailingHeaderCell />}
            </TableRow>
        </TableHead>
    )
}
